'use client';

import { motion } from "framer-motion";
import { TallerButtonLink } from "@/components/taller/button";
import { useTranslations } from "@/i18n/client";
import { SplineViewer } from "@/components/ui/spline-viewer";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.21, 0.47, 0.32, 0.98] as const,
    },
  },
};

/** Modern Hero section with radial gradient background and bold typography. */
export function Hero() {
  const { t, isLoading } = useTranslations();
  const hero = t('hero');

  if (isLoading) return null;

  const pillars = hero.pillars ?? [
    { key: 'Sin pago inicial', value: 'Pagas al ver resultados' },
    { key: 'SEO desde día uno', value: 'Arquitectura optimizada' },
    { key: 'Entrega en días', value: 'No en semanas' },
  ];

  return (
    <section
      className="relative pt-20 pb-20 md:pt-24 md:pb-32 overflow-hidden min-h-[90vh] flex flex-col justify-center"
      style={{
        background:
          "radial-gradient(circle at top left, var(--color-accent-glow), var(--color-bg) 60%)",
      }}
    >
      {/* Spline Background Container - High Z-index but buttons even higher */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 0.8, scale: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
        className="absolute top-0 right-0 w-full md:w-[80%] h-full pointer-events-auto z-20 md:translate-x-[15%] translate-y-[5%] md:-translate-y-[10%] transition-opacity duration-1000"
      >
        <SplineViewer url="https://prod.spline.design/r9t1jEmkMYsAP4lo/scene.splinecode" delay={4000} />
      </motion.div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 relative z-30 pointer-events-none"
      >
        {/* Badge */}
        <motion.div 
          variants={itemVariants}
          className="inline-flex items-center gap-3 border border-[var(--color-border)] rounded-full px-4 py-1.5 mb-10 bg-[var(--color-surface)]/50 backdrop-blur-sm pointer-events-auto"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--color-accent)] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--color-accent)]"></span>
          </span>
          <span className="font-mono text-[11px] tracking-widest uppercase text-[var(--color-mute)]">
            {hero.badge || 'Tu Partner Digital · Madrid'}
          </span>
        </motion.div>

        <motion.h1 
          variants={itemVariants}
          className="taller-page-title max-w-[900px] drop-shadow-md select-none"
        >
          {hero.h1Part1 || 'Tu web, '}
          <span className="text-[var(--color-accent)]">{hero.h1Accent || 'viva'}</span>
          <br />
          {hero.h1Part2 || 'en cuestión de días.'}
        </motion.h1>

        <motion.div 
          variants={itemVariants}
          className="mt-12 flex flex-col md:flex-row md:items-end justify-between gap-10"
        >
          <p className="font-sans text-[20px] leading-[1.5] text-[var(--color-mute)] max-w-[560px] m-0 select-none">
            {hero.subtitle || 'Diseño web profesional para pymes y autónomos en Madrid.'}
          </p>
          <div className="flex gap-4 items-center flex-wrap relative z-50 pointer-events-auto">
            <TallerButtonLink variant="primary" size="lg" href="/contacto">
              {hero.ctaPrimary || 'Empieza gratis →'}
            </TallerButtonLink>
            <TallerButtonLink variant="ghost" size="lg" href="/casos-exito">
              {hero.ctaSecondary || 'Ver casos'}
            </TallerButtonLink>
          </div>
        </motion.div>

        {/* Pillars row */}
        <motion.div 
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20 relative z-50 pointer-events-auto"
        >
          {pillars.map((pillar, i) => (
            <div
              key={pillar.key}
              className="p-6 md:p-8 rounded-[var(--radius-lg)] bg-[var(--color-surface)]/30 backdrop-blur-md border border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface)]/50 group"
            >
              <div className="w-10 h-10 rounded-full bg-[var(--color-accent)]/10 flex items-center justify-center text-[var(--color-accent)] mb-5 group-hover:scale-110 transition-transform">
                {i === 0 && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>}
                {i === 1 && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>}
                {i === 2 && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>}
              </div>
              <div className="font-sans font-bold text-[20px] tracking-tight text-[var(--color-ink)]">
                {pillar.key}
              </div>
              <div className="font-sans text-[14px] leading-relaxed text-[var(--color-mute)] mt-2">
                {pillar.value}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
